/* eslint-disable max-lines-per-function */
import React from "react";
import { gql } from "graphql-tag";
import { useQuery } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import MaterialTable from "@material-table/core";
import { ServerError } from "components/graphql-views";
import { getLists, getOrganizations, getLists_interestLists as InterestListData } from "types/gql-types";

const GET_LISTS = gql`
  query getLists {
    interestLists(includeArchived: true) {
      id
      name
      type
      organizationId
      isArchived
    }
  }
`;
const GET_ORGS = gql`
  query getOrganizations {
    organizations {
      id
      name
    }
  }
`;

const exportToCSV = (
  listsData?: InterestListData[],
  organizationNamesMap?: Record<string, string>,
) => {
  if (!listsData || !organizationNamesMap) return;

  const csvContent = [
    ["Interest List Name", "Organization Name", "Type", "Is Archived"],
    ...listsData.map(list => [
      list.name,
      organizationNamesMap[list.organizationId] || "Unknown",
      list.type,
      list.isArchived ? "Archived" : "Active",
    ]),
  ].map(row => row.join(",")).join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", "interest-lists-export.csv");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const Lists: React.FC = () => {
  const navigate = useNavigate();
  const { loading, error, data } = useQuery<getLists>(GET_LISTS, { fetchPolicy: "cache-and-network" });
  const { loading: loadingOrgs, error: errorOrgs, data: dataOrgs } = useQuery<getOrganizations>(GET_ORGS, { fetchPolicy: "cache-and-network" });
  if (error) return <ServerError desc={error.message} />;
  if (errorOrgs) return <ServerError desc={errorOrgs.message} />;
  if (!data || !dataOrgs) return null;
  const organizationNamesMap = dataOrgs.organizations
    .reduce((acc, { id, name }) => ({
      ...acc,
      [id]: name,
    }), {} as Record<string, string>);
  return (
    <MaterialTable
      actions={[
        {
          icon: "add",
          tooltip: "Create Interest List",
          isFreeAction: true,
          onClick: () => navigate("/dash/admin/lists/new"),
        },
        {
          icon: "save_alt",
          tooltip: "Export to CSV",
          isFreeAction: true,
          onClick: () => exportToCSV(data?.interestLists, organizationNamesMap),
        },
      ]}
      title="Interest Lists"
      isLoading={loading || loadingOrgs}
      data={data && data.interestLists}
      options={{
        groupTitle: ({ value }: any) => organizationNamesMap[value] || "Unknown",
        grouping: true,
        pageSize: 50,
        pageSizeOptions: [50, 100, 200, 500],
      }}
      columns={[
        {
          grouping: false,
          field: "name",
          title: "Interest List Name",
          defaultSort: "asc",
          render: ({ id, name }) => <Link to={`/dash/admin/lists/${id}`}>{name}</Link>,
        }, {
          field: "organizationId",
          title: "Organization Name",
          render: ({ organizationId }) => organizationNamesMap[organizationId],
        }, {
          field: "isArchived",
          title: "Is Archived",
          render: ({ isArchived }) => (isArchived ? "Archived" : "Active"),
          customFilterAndSearch: (filterText, row) => {
            const archivedText = row.isArchived ? "Archived" : "Active";
            return archivedText.toLowerCase().includes(filterText.toLowerCase());
          },
        },
      ]} />
  );
};

export default Lists;
